import { isEmpty } from 'lodash';

import { VARIANTS } from '@jobcase/react_pattern_library/components/Toaster/Toaster';

import { getAllSubOrgs } from '../../entities/subOrganizations/actions';
import { getAllJobGroups } from '../../entities/jobGroups/actions';
import { getProfileKey } from '../../authentication/selectors';
import { setNotification } from '../../notification/actions';

import JobsAPI from '../../../../utils/api/jobsAPI';
import { omit } from '../../../../utils/utils';

const jobsAPIFunc = JobsAPI();

export const TYPES = {
  LOAD_SUB_ORGS_REQUEST: 'dashboard/LOAD_SUB_ORGS_REQUEST',
  LOAD_SUB_ORGS_SUCCESS: 'dashboard/LOAD_SUB_ORGS_SUCCESS',
  LOAD_SUB_ORGS_ERROR: 'dashboard/LOAD_SUB_ORGS_ERROR',
  LOAD_JOB_GROUPS_REQUEST: 'dashboard/LOAD_JOB_GROUPS_REQUEST',
  LOAD_JOB_GROUPS_SUCCESS: 'dashboard/LOAD_JOB_GROUPS_SUCCESS',
  LOAD_JOB_GROUPS_ERROR: 'dashboard/LOAD_JOB_GROUPS_ERROR',
  LOAD_JOBS_REQUEST: 'dashboard/LOAD_JOBS_REQUEST',
  LOAD_JOBS_SUCCESS: 'dashboard/LOAD_JOBS_SUCCESS',
  LOAD_JOBS_ERROR: 'dashboard/LOAD_JOBS_ERROR',
  LOAD_TABLE_JOBS_REQUEST: 'dashboard/LOAD_TABLE_JOBS_REQUEST',
  LOAD_TABLE_JOBS_SUCCESS: 'dashboard/LOAD_TABLE_JOBS_SUCCESS',
  LOAD_TABLE_JOBS_ERROR: 'dashboard/LOAD_TABLE_JOBS_ERROR',
  CLEAR_TABLE_JOBS_STATE: 'dashboard/CLEAR_TABLE_JOBS_STATE',
};

export const loadSubOrgs = (options, overwrite = false) => async (dispatch) => {
  dispatch({ type: TYPES.LOAD_SUB_ORGS_REQUEST });
  try {
    let { data, pagination } = await dispatch(getAllSubOrgs(options));

    data = Object.values(data || {}).reduce((result, entity) => ({
      ...result,
      [entity.id]: {
        id: entity.id,
        name: entity.name,
        totalJobGroups: entity.job_groups || 0,
        totalJobs: entity.total_jobs || 0,
      },
    }), {});

    pagination = {
      hasNextPage: !pagination.last,
      pageNumber: pagination.number + 1,
    };

    const payload = { data, pagination, overwrite };
    dispatch({ type: TYPES.LOAD_SUB_ORGS_SUCCESS, payload });
  } catch (error) {
    dispatch({ type: TYPES.LOAD_SUB_ORGS_ERROR, payload: error });
  }
};

export const loadJobGroups = (options, profileKey, overwrite = false) => async (dispatch) => {
  dispatch({ type: TYPES.LOAD_JOB_GROUPS_REQUEST });
  try {
    const response = await dispatch(getAllJobGroups(options, profileKey));
    const { data: { content, number, last } } = response;

    const data = content.reduce((result, entity) => ({
      ...result,
      [entity.id]: {
        id: entity.id,
        name: entity.name,
        totalJobs: entity.active_jobs,
        subOrgId: entity.sub_organization_id,
      },
    }), {});

    const pagination = {
      hasNextPage: !last,
      pageNumber: number + 1,
    };

    const payload = { data, pagination, overwrite };

    dispatch({ type: TYPES.LOAD_JOB_GROUPS_SUCCESS, payload });

    return payload;
  } catch (error) {
    dispatch({ type: TYPES.LOAD_JOB_GROUPS_ERROR, payload: error });
    return null;
  }
};

export const loadJobs = (options, overwrite = false) => async (dispatch, getState) => {
  dispatch({ type: TYPES.LOAD_JOBS_REQUEST });
  const profileKey = getProfileKey(getState());
  try {
    const response = await jobsAPIFunc.getJobs(options, profileKey);
    // eslint-disable-next-line prefer-const
    let { data: { content, pagination } } = response;

    const data = content.reduce((result, entity) => ({
      ...result,
      [entity.id]: {
        id: entity.id,
        name: entity.title,
        jobGroupId: entity.job_group_id,
        subOrgId: entity.sub_organization_id,
      },
    }), {});

    pagination = {
      hasNextPage: pagination?.page_current !== pagination?.page_total,
      pageNumber: pagination?.page_current,
    };

    const payload = { data, pagination, content, overwrite };

    dispatch({ type: TYPES.LOAD_JOBS_SUCCESS, payload });

    return payload;
  } catch (error) {
    dispatch({ type: TYPES.LOAD_JOBS_ERROR, payload: error });
    return null;
  }
};

export const loadAllJobs = options => async (dispatch, getState) => {
  const newOptions = { ...options };
  if (newOptions?.is_active?.length === 2) delete newOptions.is_active;
  dispatch({ type: TYPES.LOAD_TABLE_JOBS_REQUEST, payload: { page: newOptions.page_current } });
  const profileKey = getProfileKey(getState());
  try {
    const response = await jobsAPIFunc.getJobs(newOptions, profileKey);
    const { data: { content, pagination } } = response;
    const data = content.reduce((result, entity) => ({ ...result, [entity.id]: entity }), {});
    dispatch({ type: TYPES.LOAD_TABLE_JOBS_SUCCESS, payload: { content: data, pagination } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.LOAD_TABLE_JOBS_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading jobs. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (error.error_code !== 403) {
      dispatch(setNotification('An error occurred while loading jobs.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const loadAllJobsSortedByCandidateCount = options => async (dispatch, getState) => {
  const organizationId = options.organization_id;
  const newOptions = { ...options };
  if (newOptions?.is_active?.length === 2) delete newOptions.is_active;
  dispatch({ type: TYPES.LOAD_TABLE_JOBS_REQUEST, payload: { page: newOptions.page_current } });
  const profileKey = getProfileKey(getState());
  try {
    const candidatesCountResponse = await jobsAPIFunc.getJobsCandidatesCount(organizationId, options);
    const jobIds = (candidatesCountResponse?.data?.content || []).map(obj => obj.id);
    if (!jobIds.length) {
      dispatch({ type: TYPES.LOAD_TABLE_JOBS_SUCCESS, payload: { content: {}, pagination: candidatesCountResponse.data.pagination } });
      return candidatesCountResponse;
    }
    newOptions.job_id = [...(newOptions.jobId || []), ...jobIds];
    const response = await jobsAPIFunc.getJobs(omit(['page_current'], newOptions), profileKey);
    const { data: { content } } = response;
    const data = content.reduce((result, entity) => ({ ...result, [entity.id]: entity }), {});
    dispatch({ type: TYPES.LOAD_TABLE_JOBS_SUCCESS, payload: { content: data, pagination: candidatesCountResponse.data.pagination } });
    return candidatesCountResponse;
  } catch (error) {
    dispatch({ type: TYPES.LOAD_TABLE_JOBS_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading jobs. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (error.error_code !== 403) {
      dispatch(setNotification('An error occurred while loading jobs.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const clearTableJobsState = () => (dispatch) => {
  dispatch({ type: TYPES.CLEAR_TABLE_JOBS_STATE });
};

export default {
  TYPES,
  loadSubOrgs,
  loadAllJobs,
  loadAllJobsSortedByCandidateCount,
  clearTableJobsState,
};
